import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, TrendingDown, Award, AlertCircle } from "lucide-react";
import { fromZonedTime } from "date-fns-tz";
import { useShopTimezone } from "@/contexts/TimezoneContext";
import { useTechnicianSummary } from "@/hooks/kpi/useTechnicianSummary";

interface TechnicianPerformanceCardProps {
  technicianProfileId: string;
  shopId: string;
  /**
   * Optional shop-local date range (yyyy-MM-dd) used to scope the
   * canonical `kpi_technician_summary` RPC's `[start_at, end_at)`
   * window. When omitted, the component falls back to a wide window
   * approximating lifetime semantics so existing callers without a
   * date range continue to work.
   */
  startDate?: string;
  endDate?: string;
}

export function TechnicianPerformanceCard({
  technicianProfileId,
  shopId,
  startDate,
  endDate,
}: TechnicianPerformanceCardProps) {
  const { shopTimezone } = useShopTimezone();

  // Resolve technicianProfileId → shop_technician_id. The composite
  // summary RPC takes `tech_id` (a shop_technician_id), not a profile
  // ID, so this lookup remains. Pulling it to the top level keeps the
  // canonical hook composable.
  const { data: techId } = useQuery({
    queryKey: ["employment-tech-id", technicianProfileId, shopId],
    queryFn: async () => {
      const { data } = await supabase
        .from("technician_employment_history")
        .select("shop_technician_id")
        .eq("technician_profile_id", technicianProfileId)
        .eq("shop_id", shopId)
        .maybeSingle();
      return data?.shop_technician_id ?? null;
    },
    enabled: !!technicianProfileId && !!shopId,
  });

  // K-T-6: canonical composite tuple from `kpi_technician_summary`
  // RPC. Replaces the prior pair of local useQuery calls (tickets +
  // ticket_jobs) AND the standalone `useTechnicianEfficiency` hook.
  // After K-T-6 the card issues exactly two server round-trips: the
  // employment lookup above and this composite RPC.
  //
  // Date-range conversion mirrors KPI-R71-FIX (see
  // `InspectionPerformanceCard` for rationale): shop-local yyyy-MM-dd
  // strings → UTC ISO timestamps so the half-open window aligns with
  // the shop's calendar day.
  const startAt = startDate
    ? fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString()
    : "1900-01-01T00:00:00Z";
  const endAt = endDate
    ? fromZonedTime(`${endDate}T23:59:59.999`, shopTimezone).toISOString()
    : "2099-12-31T23:59:59.999Z";

  const { data: summary, isLoading } = useTechnicianSummary({
    shopId,
    techId: techId ?? "",
    startAt,
    endAt,
  });

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="h-24 animate-pulse bg-muted rounded" />
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  // K-T-6: every KPI value comes from the canonical summary tuple.
  // Each "rate" KPI honors its own gated boolean — null when sample
  // size < MIN_KPI_SAMPLE_SIZE. `avg_hours_per_ticket` and
  // `jobs_completed` are never gated.
  const efficiencyRatio = summary.efficiency_gated
    ? null
    : summary.efficiency_ratio;
  const comebackRate = summary.comeback_gated ? null : summary.comeback_rate;
  const diagnosticAccuracy = summary.diagnostic_gated
    ? null
    : summary.diagnostic_accuracy;
  const avgHoursPerTicket = summary.avg_hours_per_ticket;
  const jobsCompleted = summary.jobs_completed;

  const getEfficiencyBadge = (eff: number | null) => {
    if (eff === null) return <Badge variant="outline">Insufficient data</Badge>;
    if (eff >= 1.0)
      return (
        <Badge variant="default" className="gap-1">
          <TrendingUp className="h-3 w-3" />
          Efficient
        </Badge>
      );
    if (eff >= 0.85) return <Badge variant="secondary" className="gap-1">On Track</Badge>;
    return (
      <Badge variant="destructive" className="gap-1">
        <TrendingDown className="h-3 w-3" />
        Below Target
      </Badge>
    );
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Performance Metrics</h3>
        <Award className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Efficiency (Book Time)</span>
          {getEfficiencyBadge(efficiencyRatio)}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Book Time Ratio</span>
          <span className="font-mono text-sm font-medium text-foreground">
            {efficiencyRatio === null ? "—" : `${(efficiencyRatio * 100).toFixed(0)}%`}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Comeback Rate</span>
          <span
            className={`font-mono text-sm font-medium ${
              comebackRate !== null && comebackRate > 0.05
                ? "text-destructive"
                : "text-foreground"
            }`}
          >
            {comebackRate === null ? "—" : `${(comebackRate * 100).toFixed(1)}%`}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Diagnostic Accuracy</span>
          <span className="font-mono text-sm font-medium text-foreground">
            {diagnosticAccuracy === null
              ? "—"
              : `${(diagnosticAccuracy * 100).toFixed(0)}%`}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Avg Hours per Ticket</span>
          <span className="font-mono text-sm font-medium text-foreground">
            {avgHoursPerTicket.toFixed(2)}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-sm text-muted-foreground">Jobs Completed</span>
          <span className="font-mono text-sm font-medium text-foreground">
            {jobsCompleted}
          </span>
        </div>
      </div>

      {comebackRate !== null && comebackRate > 0.05 && (
        <div className="mt-3 flex items-start gap-2 p-2 bg-destructive/10 rounded-md">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
          <p className="text-xs text-destructive">High comeback rate detected</p>
        </div>
      )}
    </Card>
  );
}
